import { useState, useEffect, useCallback } from 'react'
import { useToast } from './useToast'
import { 
  getActions as getActionsAPI,
  executeAction as executeActionAPI,
  getRunningActions, 
  stopAction as stopActionAPI 
} from '@/shared/lib/api'

export interface Action {
  id: string
  label: string
  description: string
  category: string
  icon: string
  color: string
  requires_confirmation: boolean
  is_background: boolean
}

export function useActions() {
  const [actions, setActions] = useState<Action[]>([])
  const [loading, setLoading] = useState(false)
  const [runningActions, setRunningActions] = useState<Set<string>>(new Set())
  const { showToast } = useToast()

  // Fetch available actions from backend
  useEffect(() => {
    getActionsAPI()
      .then(data => setActions(data))
      .catch(err => {
        console.error('Failed to fetch actions:', err)
        showToast('Failed to load actions', 'error')
      })
  }, [showToast])

  // Fetch running actions status
  const updateRunningStatus = useCallback(async () => {
    try {
      const running = await getRunningActions()
      setRunningActions(new Set(Object.keys(running)))
    } catch {
      // Silently fail - not critical
    }
  }, [])

  // Initial fetch once on mount
  useEffect(() => {
    updateRunningStatus()
  }, [updateRunningStatus])

  // Poll every 2 seconds only when actions are running
  useEffect(() => {
    if (runningActions.size === 0) return
    const interval = setInterval(updateRunningStatus, 2000)
    return () => clearInterval(interval)
  }, [runningActions.size, updateRunningStatus])

  // Execute an action
  const executeAction = async (actionId: string, params?: Record<string, unknown>) => {
    setLoading(true)
    try {
      const result = await executeActionAPI(actionId, params)
      
      if (result.success) {
        const action = actions.find(a => a.id === actionId)
        const isBackground = action?.is_background || false
        
        if (isBackground && result.result?.status === 'running') {
          // Background action started
          setRunningActions(prev => new Set(prev).add(actionId))
          showToast(`Action "${result.action_id}" started`, 'success')
        } else {
          showToast(`Action "${result.action_id}" completed successfully`, 'success')
        }
      } else {
        showToast(`Action failed: ${result.error}`, 'error')
      }
      
      return result
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to execute action'
      showToast(errorMessage, 'error')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Stop a running background action
  const stopAction = useCallback(async (actionId: string) => {
    setLoading(true)
    try {
      const result = await stopActionAPI(actionId)
      
      if (result.success) {
        setRunningActions(prev => {
          const next = new Set(prev)
          next.delete(actionId)
          return next
        })
        showToast(`Action "${actionId}" stopped`, 'success')
      } else {
        showToast(`Failed to stop action: ${result.error}`, 'error')
      }
      
      return result
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to stop action'
      showToast(errorMessage, 'error')
      throw error
    } finally {
      setLoading(false)
    }
  }, [showToast])

  // Check if an action is running
  const isActionRunning = useCallback((actionId: string) => {
    return runningActions.has(actionId)
  }, [runningActions])

  return {
    actions,
    executeAction,
    stopAction,
    isActionRunning,
    runningActions: Array.from(runningActions),
    loading
  }
}
